/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Button from 'common/Button';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import pageActions from 'core/page/actions';
import dataActions from 'core/data/actions';
import groupActions from 'core/groups/actions';
import dialogActions from 'core/dialogs/actions';
import BottomContent from 'common/BottomContent';
import hasError from 'utils/hasError';
import ErrorMsg from 'common/ErrorMsg';
import ListGroup from 'containers/ListGroup';
import SignerstatusRules from 'common/SignerstatusRules';

import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    addUser: {
      margin: '50px auto 0',
    },
    back: {
      display: 'block',
      margin: '30px auto 0',
    },
    desktopLabel: {
      ...common.desktopLabel,
      fontSize: 14,
      fontWeight: theme.typography.fontWeights.normal,
      margin: [0, 'auto', theme.spacing.xs],
      maxWidth: 440,
    },
    h2: {
      fontSize: 20,
      textAlign: 'center',
    },
    hidden: {
      display: 'none',
    },
    mobileLabel: {
      ...common.mobileLabel,
      fontSize: 14,
      fontWeight: theme.typography.fontWeights.normal,
      margin: [0, 'auto', theme.spacing.xs],
      maxWidth: 440,
    },
  };
};

const BusinessUsersBare = ({
  backButtonLabel,
  bottomContent,
  classes,
  disableButton,
  hasFields,
  label,
  labelMobile,
  listGroup,
  name: groupName,
  numItems,
  onAdd,
  onBack,
  onDone,
  onEdit,
  removeSigner,
  resources,
  showAddButton,
  showBackButton,
  subText,
}) => (
  <>
    <legend
      className={classnames('grp-label', classes.label)}
      role={!hasFields ? 'presentation' : undefined}
    >
      <RawHtml type="span" className={classnames('desktop', classes.desktopLabel)}>
        {label}
      </RawHtml>
      <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
        {labelMobile}
      </RawHtml>
    </legend>
    <div className="business-users-group-view">
      <RawHtml className={classnames('content', classes.content, classes.hidden)}>
        {subText}
      </RawHtml>

      <ErrorMsg name={groupName} type="group" />

      <ListGroup groupName={groupName} onRemove={removeSigner} onDone={onDone} onEdit={onEdit} />

      <SignerstatusRules />

      {showAddButton && (
        <Button
          className={classnames('btn btn-mobile btn-tertiary btn-add-user', classes.addUser)}
          data-name="btn-add-user"
          variant="secondary"
          onClick={onAdd}
          style={{ display: resources.hasMaxBusinessSigners === 'true' ? 'none' : 'block' }}
          disabled={numItems >= listGroup.maxItems || disableButton}
        >
          {listGroup.addButtonLabel}
        </Button>
      )}

      {showBackButton && (
        <RawHtml
          className={classnames(
            'btn btn-mobile btn-tertiary btn-grp-back mt-5 btn-edit-users',
            classes.button,
            classes.back,
          )}
          data-name="btn-grp-back"
          type="button"
          onClick={onBack}
          variant="secondary"
        >
          {backButtonLabel}
        </RawHtml>
      )}

      <BottomContent className="bottomContent">{bottomContent}</BottomContent>
    </div>
  </>
);

BusinessUsersBare.propTypes = {
  backButtonLabel: PropTypes.string,
  bottomContent: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  disableButton: PropTypes.bool.isRequired,
  hasFields: PropTypes.bool,
  label: PropTypes.string,
  labelMobile: PropTypes.string,
  listGroup: PropTypes.shape({
    addButtonLabel: PropTypes.string,
    maxItems: PropTypes.number,
  }).isRequired,
  name: PropTypes.string.isRequired,
  numItems: PropTypes.number,
  onAdd: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  removeSigner: PropTypes.func.isRequired,
  resources: PropTypes.objectOf(PropTypes.string).isRequired,
  showAddButton: PropTypes.bool,
  showBackButton: PropTypes.bool,
  subText: PropTypes.string,
};

BusinessUsersBare.defaultProps = {
  backButtonLabel: 'Back',
  bottomContent: '',
  hasFields: false,
  label: '',
  labelMobile: '',
  numItems: 0,
  showAddButton: false,
  showBackButton: false,
  subText: '',
};

const mapStateToProps = ({ data, fields, groups, ui }, { name }) => {
  const group = groups[name];
  return {
    backButtonLabel: group.backButtonLabel,
    bottomContent: group.bottomContent,
    disableButton:
      (fields && hasError(fields)) ||
      Object.values(data[`${name}___list`])?.length > group.listGroup.maxItems,
    hasFields: group?.fields?.length > 0 ?? false,
    label: group.label,
    labelMobile: group.labelMobile,
    listGroup: group.listGroup,
    loading: !!(ui.groupLoading || ui.fieldLoading || ui.appLoading || ui.pageLoading),
    numItems: data[`${name}___list`].length,
    resources: group.resources,
    showAddButton: !data.needBackButton,
    showBackButton: data.needBackButton,
    subText: group.subText,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onAdd: () => dispatch(pageActions.addSignerStatus()),
  onBack: () => dispatch(pageActions.addSignerStatusBack()),
  onDone: (...args) => dispatch(groupActions.update(...args)),
  onEdit: (...args) => dispatch(dataActions.changeListGroupData(...args)),
  removeSigner: (...args) => {
    dispatch(
      dialogActions.open('confirm', {
        message: 'Are you sure you want to remove this user?',
        onConfirm: () => dispatch(pageActions.removeSigner(...args)),
      }),
    );
  },
});

const BusinessUsersStyled = withStyles(styles)(BusinessUsersBare);

export default connect(mapStateToProps, mapDispatchToProps)(BusinessUsersStyled);
